import { all, call, put, fork, takeEvery, select } from 'redux-saga/effects';
import {
    fetchFeaturesSuccess,
    showFeatureMessage,
    onSaveFeatureSuccess,
    onDeleteFeatureSuccess,
    addFavouriteFeatureSuccess,
    onFeatureDependencyUpdateSuccess,
    onFetchSelectFeatureDependenciesSuccess,
} from 'actions/feature';
import {
    ON_SAVE_FEATURE,
    FETCH_ALL_FEATURE,
    ON_DELETE_FEATURE,
    ADD_FAVOURITE_FEATURE,
    ON_FEATURE_INWARD_DEPENDENCY_UPDATE,
    ON_FEATURE_OUTWARD_DEPENDENCY_UPDATE,
    ON_FETCH_SELECT_FEATURE_INWARD_DEPENDENCIES,
    ON_FETCH_SELECT_FEATURE_OUTWARD_DEPENDENCIES
} from 'constants/ActionTypes';
import { fetchDataRequest, addOrUpdateDataRequest, updateDataRequest, deleteDataRequest } from 'services/ApiService';
import { apiErrorMessageFormatter } from 'services/errorService';
import { removeEmptyEntries } from 'services/objectService';
import { ORGID_PATH_PARAM } from 'constants/api';
import { mapInwardDependenciesToSelectionData, mapOutwardDependenciesToSelectionData, mapSelectionsToDependencyData } from 'services/dependencyService';

const currentOrg = (state) => state.orgs.selectedOrg;
const getFeatureList = (state) => state.features.allFeature;
const getSelectedFeature = (state) => state.features.selectedFeature;
const getDependencyList = (state) => state.features.dependencyList;


export function* fetchFeatures() {
    try {
        const selectedOrg = yield select(currentOrg);
        const fetchedFeatures = yield call(
            fetchDataRequest,
            'features',
            [ORGID_PATH_PARAM],
            [selectedOrg?.id]
        ) || [];
        yield put(fetchFeaturesSuccess(fetchedFeatures));
    } catch (error) {
        yield put(showFeatureMessage(apiErrorMessageFormatter(error)));
    }
}

export function* deleteFeature({ payload }) {
    try {
        // payload should be the featureId
        const selectedOrg = yield select(currentOrg);
        yield call(
            deleteDataRequest,
            'deleteFeature',
            'Deleting feature '.concat(payload),
            [ORGID_PATH_PARAM, 'featureId'],
            [selectedOrg?.id, payload]
        ) || [];
        yield put(onDeleteFeatureSuccess(payload)); // just dispatch the id as the reducer will remove from the item from the list
    } catch (error) {
        yield put(showFeatureMessage(apiErrorMessageFormatter(error)));
    }
}

export function* saveFeature({ payload }) {
    try {
        const selectedOrg = yield select(currentOrg);
        const allFeatures = yield select(getFeatureList);
        let finalPayload = { ...payload };
        // check the orgid of the payload - it will be empty for a new payload - populate from the selectedOrg - otherwise leave it as is
        if (payload?.orgid === '') {
            finalPayload = { ...payload, orgid: selectedOrg?.id };
        }
        finalPayload = removeEmptyEntries(finalPayload); // NOTE - if adding a new feature - the id would not have been populated yet
        const savedFeatures = yield call(
            addOrUpdateDataRequest,
            allFeatures,
            finalPayload,
            'addFeature',
            'updateFeature',
            [ORGID_PATH_PARAM],
            [selectedOrg.id]);
        yield put(onSaveFeatureSuccess(savedFeatures));
    } catch (error) {
        yield put(showFeatureMessage(apiErrorMessageFormatter(error)));
    }
}

/**
 * This is when the user selects the star on the list page - update the starred attribute of the entity
 * @param {*} param0 
 */
export function* addFavouriteFeature({ payload }) {
    try {
        const allFeatures = yield select(getFeatureList);
        const selectedOrg = yield select(currentOrg);
        const filtered = allFeatures.filter((entry) => entry?.id === payload?.id);

        if (filtered?.length > 0) {
            const entry = filtered[0];
            const updatedFeature = {
                ...entry,
                starred: !payload.starred
            };
            const auditMsg = 'Updating starred setting to '.concat(updatedFeature?.starred).concat(' for feature ').concat(updatedFeature?.id);
            yield call(
                updateDataRequest,
                updatedFeature,
                'updateFeature',
                auditMsg,
                [ORGID_PATH_PARAM],
                [selectedOrg.id],
            );
            // now just pass on the original payload as the reducer will filter the list as per the state of the selection
            yield put(addFavouriteFeatureSuccess(payload));
        }
    } catch (error) {
        yield put(showFeatureMessage(apiErrorMessageFormatter(error)));
    }
}
/**
 * 
 * @param {Array[Object]} payload - an array of selection objects (refer to selectorByIdBuilder)
 * [{id:otherfeatureId,
 *   name:'name',
 *   selected:true,
 *   originalValue:false}, ...]
 * 
 * "dependencies": [
            {
                "outward": {
                    "id": "9648cb65923bb7ba0845014820190617"
                },
                "id": "2578cb65923bb7ba0845014820176298"
            },
            {
                "id": "2578cb65923bb7ba0845014820176435",
                "inward": {
                    "id": "3875cb65923bb7ba0845014820190617"
                }
            }
        ],
 */
export function* onFeatureInwardDependencyUpdate() {
    try {
        // go through each  - check if dependency is being added or removed and cleanup the other feature being referenced
        // then call (refer to updateProjectPriorities)
        const selectedOrg = yield select(currentOrg);
        const featureList = yield select(getFeatureList);
        const selectedFeature = yield select(getSelectedFeature);
        const dependencyList = yield select(getDependencyList);
        const updateFeatureList = yield call(updateFeatureDependencies, selectedOrg, featureList, selectedFeature, dependencyList, true);
        if (Array.isArray(updateFeatureList) && updateFeatureList.length > 0) {
            yield put(onFeatureDependencyUpdateSuccess(updateFeatureList));
            yield put(showFeatureMessage('Feature dependency selections updated successfully.'));
        } else {
            yield put(showFeatureMessage('No changes were made to the dependency selections.'));
        }
    } catch (error) {
        yield put(showFeatureMessage(apiErrorMessageFormatter(error)));
    }
}

export function* onFeatureOutwardDependencyUpdate() {
    try {
        // go through each  - check if dependency is being added or removed and cleanup the other feature being referenced
        // then call (refer to updateProjectPriorities)
        const selectedOrg = yield select(currentOrg);
        const featureList = yield select(getFeatureList);
        const selectedFeature = yield select(getSelectedFeature);
        const dependencyList = yield select(getDependencyList);
        const updateFeatureList = yield call(updateFeatureDependencies, selectedOrg, featureList, selectedFeature, dependencyList, false);
        if (Array.isArray(updateFeatureList) && updateFeatureList.length > 0) {
            yield put(onFeatureDependencyUpdateSuccess(updateFeatureList));
            yield put(showFeatureMessage('Feature dependency selections updated successfully.'));
        } else {
            yield put(showFeatureMessage('No changes were made to the dependency selections.'));
        }
    } catch (error) {
        yield put(showFeatureMessage(apiErrorMessageFormatter(error)));
    }
}

const updateFeatureDependencies = async (selectedOrg, featureList, selectedFeature, dependencyList, inward) => {
    const updatedFeatures = mapSelectionsToDependencyData(featureList, selectedFeature, dependencyList, inward);
    if (Array.isArray(updatedFeatures) && updatedFeatures.length > 0) {
        // update the feature list to put back into the store
        const updateFeatureList = featureList.map((feature) => {
            const currentIndex = updatedFeatures.findIndex((updated) => updated?.id === feature?.id);
            if (currentIndex === -1) {
                // not updated so return the current value
                return feature;
            } else {
                return updatedFeatures[currentIndex];
            }
        });
        // change the payload to include the orgid for security reasons - checks against the path param
        const newPayload = {
            orgid: selectedOrg?.id,
            features: updatedFeatures
        };
        const auditLogMessage = 'Updating feature '.concat(selectedFeature?.id).concat(' ').concat(inward ? 'inward' : 'outward').concat(' dependencies');
        const auditLog = { message: auditLogMessage, name: selectedFeature?.name, description: JSON.stringify(selectedFeature?.dependencies) };
        await updateDataRequest(newPayload, 'updateFeatures', auditLog, [ORGID_PATH_PARAM], [selectedOrg?.id]);
        return updateFeatureList;
    }
    return [];
};

export function* onFetchSelectFeatureInwardDependencies() {
    const featureList = yield select(getFeatureList);
    const selectedFeature = yield select(getSelectedFeature);
    const selectionDependencies = mapInwardDependenciesToSelectionData(featureList, selectedFeature) || null;
    yield put(onFetchSelectFeatureDependenciesSuccess(selectionDependencies));
}

export function* onFetchSelectFeatureOutwardDependencies() {
    const featureList = yield select(getFeatureList);
    const selectedFeature = yield select(getSelectedFeature);
    const selectionDependencies = mapOutwardDependenciesToSelectionData(featureList, selectedFeature) || null;
    yield put(onFetchSelectFeatureDependenciesSuccess(selectionDependencies));
}

export function* fetchFeaturesSaga() {
    yield takeEvery(FETCH_ALL_FEATURE, fetchFeatures);
}

export function* saveFeatureSaga() {
    yield takeEvery(ON_SAVE_FEATURE, saveFeature);
}

export function* deleteFeatureSaga() {
    yield takeEvery(ON_DELETE_FEATURE, deleteFeature);
}

export function* addFavouriteFeatureSaga() {
    yield takeEvery(ADD_FAVOURITE_FEATURE, addFavouriteFeature);
}

export function* onFeatureInwardDependencyUpdateSaga() {
    yield takeEvery(ON_FEATURE_INWARD_DEPENDENCY_UPDATE, onFeatureInwardDependencyUpdate);
}

export function* onFeatureOutwardDependencyUpdateSaga() {
    yield takeEvery(ON_FEATURE_OUTWARD_DEPENDENCY_UPDATE, onFeatureOutwardDependencyUpdate);
}

export function* onFetchSelectFeatureInwardDependenciesSaga() {
    yield takeEvery(ON_FETCH_SELECT_FEATURE_INWARD_DEPENDENCIES, onFetchSelectFeatureInwardDependencies);
}

export function* onFetchSelectFeatureOutwardDependenciesSaga() {
    yield takeEvery(ON_FETCH_SELECT_FEATURE_OUTWARD_DEPENDENCIES, onFetchSelectFeatureOutwardDependencies);
}


export default function* rootSaga() {
    yield all([
        fork(fetchFeaturesSaga),
        fork(saveFeatureSaga),
        fork(deleteFeatureSaga),
        fork(addFavouriteFeatureSaga),
        fork(onFeatureInwardDependencyUpdateSaga),
        fork(onFeatureOutwardDependencyUpdateSaga),
        fork(onFetchSelectFeatureInwardDependenciesSaga),
        fork(onFetchSelectFeatureOutwardDependenciesSaga),
    ]);
}


// WEBPACK FOOTER //
// ./sagas/feature.js